// [COPIAR ARQUIVO]
//
// /* GS://PROTOCOLO
//  * Arquivo: C:\Users\Usuario\GoldenSisters_Project\goldensisters_admin_web\src\pages\NewPurchasePage.jsx
//  * Dossiê de Projeto e Colaboração: Ecossistema GoldenSisters
//  * Versão: 6.0.0 – Integração MoneyInput no campo Custo Bruto da Peça (COMPRA_BRUTO).
//  * Impacto da Versão: [src/pages/NewPurchasePage.jsx]
//  * Data: 22/11/2025
//  * Responsável pela Alteração: ChatGPT (conforme Protocolo)
//  *
//  * Mudanças desta versão:
//  * - ADICIONADO: Integração do componente MoneyInput no campo "Custo Bruto da Peça (R$)".
//  * - CRIADO: Handler handleItemMoneyChange para converter centavos → reais e atualizar unitCost.
//  * - PRESERVADO: Demais campos, validações e fluxo COMPRA_BRUTO inalterados.
//  */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Timestamp } from 'firebase/firestore';
// ATUALIZADO: Importações de firestoreService como named exports (sem addSupplierService)
import { addPurchase, getSuppliers, getCategories } from '../services/firestoreService';
import productService from '../services/productService'; // Importa productService para gerenciar coleção 'products'
import SupplierForm from '../components/forms/SupplierForm';
import { Plus, X, Loader2 } from 'lucide-react';
import MoneyInput from '../components/ui/MoneyInput'; // NOVO: campo monetário centesimal

// Função auxiliar para gerar SKU (mantida aqui para ser usada em tempo real no front-end)
const generateSKU = (categoryCode, supplierFriendlyCode, productSequential) => {
  const forPart = String(supplierFriendlyCode || '').padStart(3, '0');
  const numPart = String(productSequential || '').padStart(3, '0');
  return `${categoryCode || ''}${forPart}${numPart}`;
};

const NewPurchasePage = ({ openModal }) => {
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split('T')[0]);
  const [invoiceNumber, setInvoiceNumber] = useState(''); // Número da Nota Fiscal

  // Estado dos itens da compra com novos campos (dimensões e campos do produto mestre)
  // Neste fluxo (COMPRA_BRUTO), o foco é PESO (g) e CUSTO BRUTO.
  const [items, setItems] = useState([
    {
      productName: '',
      supplierId: '',
      supplierProductCode: '',
      categoryId: '',
      quantity: 1,
      unitCost: 0,
      productId: null,
      internalSku: '',
      isNewProduct: true,
      supplierName: '',
      // Dimensões – peso em gramas é crítico neste fluxo
      dimensions: { height: 0, length: 0, width: 0, weight: 0 },
      // Campos adicionais de produto mestre
      description: '',
      price: 0.0,
      images: [],
      isActive: true,
      displaySettings: { onHomepage: false, onSeparamos: false },
    },
  ]);

  const [isSaving, setIsSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const productSearchTimeout = useRef(null);

  const fetchInitialData = useCallback(async () => {
    try {
      const suppliersData = await getSuppliers();
      const categoriesData = await getCategories();
      setSuppliers(suppliersData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Erro ao carregar dados iniciais (fornecedores/categorias):', error);
      openModal(
        <p className="text-red-500">
          Não foi possível carregar os fornecedores e categorias. Verifique o console (F12) para detalhes técnicos.
        </p>,
        'Erro de Carregamento'
      );
    }
  }, [openModal]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Efeito para gerar/preencher SKU e outros dados do produto em tempo real
  useEffect(() => {
    const updateItemProductData = async () => {
      const updatedItems = await Promise.all(
        items.map(async (item) => {
          if (!item.supplierId || !item.supplierProductCode || !item.categoryId) {
            // Limpa dados de produto se info essencial não está presente
            return {
              ...item,
              productId: null,
              internalSku: '',
              isNewProduct: true,
              productName: '',
              // Limpa outros campos de produto
              dimensions: { height: 0, length: 0, width: 0, weight: 0 },
              description: '',
              price: 0.0,
              images: [],
              isActive: true,
              displaySettings: { onHomepage: false, onSeparamos: false },
            };
          }

          const selectedSupplier = suppliers.find((s) => s.id === item.supplierId);
          const selectedCategory = categories.find((c) => c.code === item.categoryId); // Usa 'code' para categoria

          if (!selectedSupplier || !selectedCategory) {
            return { ...item, productId: null, internalSku: '', isNewProduct: true };
          }

          // Tenta buscar o produto mestre pelo código do fornecedor
          let foundProduct = null;
          try {
            foundProduct = await productService.getProductBySupplierItemCode(item.supplierProductCode);
          } catch (error) {
            console.error('Erro ao buscar produto existente para geração/preenchimento:', error);
          }

          if (foundProduct) {
            // Produto existe: preenche dados do mestre (e atualiza o estado do item)
            return {
              ...item,
              productId: foundProduct.id,
              internalSku: foundProduct.sku || '',
              productName: foundProduct.name || '',
              categoryId: foundProduct.categoryId || selectedCategory.code,
              unitCost: foundProduct.cost || 0,
              isNewProduct: false,
              dimensions: foundProduct.dimensions || { height: 0, length: 0, width: 0, weight: 0 },
              description: foundProduct.description || '',
              price: foundProduct.price || 0.0,
              images: foundProduct.images || [],
              isActive: foundProduct.isActive ?? true,
              displaySettings: foundProduct.displaySettings || { onHomepage: false, onSeparamos: false },
            };
          } else {
            // Produto NOVO: gera SKU (se ainda não gerado) e marca como novo
            let generatedSku = item.internalSku;
            if (!generatedSku) {
              const nextSequentialNum = await productService.getNextProductSequentialNum(
                selectedCategory.id,
                selectedSupplier.id
              );
              generatedSku = generateSKU(selectedCategory.code, selectedSupplier.friendlyCode, nextSequentialNum);
            }
            return {
              ...item,
              productId: null,
              internalSku: generatedSku,
              isNewProduct: true,
            };
          }
        })
      );

      // Atualiza o estado apenas se houver mudanças significativas
      if (
        JSON.stringify(updatedItems.map((i) => i.productId || i.internalSku)) !==
        JSON.stringify(items.map((i) => i.productId || i.internalSku))
      ) {
        setItems(updatedItems);
      }
    };

    const hasRequiredData = suppliers.length > 0 && categories.length > 0;
    const hasItemIdentifiers = items.some(
      (item) => item.supplierId && item.supplierProductCode && item.categoryId
    );

    if (hasRequiredData && hasItemIdentifiers && !isSaving) {
      if (productSearchTimeout.current) {
        clearTimeout(productSearchTimeout.current);
      }
      productSearchTimeout.current = setTimeout(() => {
        updateItemProductData();
      }, 500);
    }
  }, [items, suppliers, categories, isSaving]);

  const handleItemChange = (index, event) => {
    const { name, value } = event.target;
    const newItems = [...items];

    newItems[index][name] =
      name === 'quantity' || name === 'unitCost' ? parseFloat(value) || 0 : value;

    // Lógica para mudança de fornecedor
    if (name === 'supplierId') {
      const selectedSupplierObj = suppliers.find((s) => s.id === value);
      newItems[index].supplierName = selectedSupplierObj ? selectedSupplierObj.name : '';
      newItems[index].productId = null;
      newItems[index].internalSku = '';
      newItems[index].isNewProduct = true;
    }

    setItems(newItems);
  };

  // NOVO: handler para campos monetários em centavos (MoneyInput)
  const handleItemMoneyChange = (index, field, cents) => {
    const reais = (Number.isFinite(cents) ? cents : 0) / 100;
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      [field]: reais,
    };
    setItems(newItems);
  };

  // Handler para mudanças nas dimensões do item
  const handleItemDimensionChange = (index, dimensionField, value) => {
    const newItems = [...items];
    const numericValue = parseFloat(value.replace(',', '.')) || 0;
    newItems[index].dimensions = {
      ...newItems[index].dimensions,
      [dimensionField]: numericValue,
    };
    setItems(newItems);
  };

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        productName: '',
        supplierId: '',
        supplierProductCode: '',
        categoryId: '',
        quantity: 1,
        unitCost: 0,
        productId: null,
        internalSku: '',
        isNewProduct: true,
        supplierName: '',
        dimensions: { height: 0, length: 0, width: 0, weight: 0 },
        description: '',
        price: 0.0,
        images: [],
        isActive: true,
        displaySettings: { onHomepage: false, onSeparamos: false },
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      const newItems = [...items];
      newItems.splice(index, 1);
      setItems(newItems);
    }
  };

  const handleAddNewSupplier = () => {
    openModal(
      <SupplierForm
        onClose={() => openModal(null)}
        onSave={async () => {
          await fetchInitialData();
          openModal(null);
        }}
      />,
      'Novo Fornecedor',
      'md'
    );
  };

  const subtotalItems = items.reduce(
    (acc, item) => acc + (Number(item.unitCost) || 0) * (Number(item.quantity) || 0),
    0
  );
  const totalPurchase = subtotalItems;

  const resetForm = () => {
    setPurchaseDate(new Date().toISOString().split('T')[0]);
    setInvoiceNumber('');
    setItems([
      {
        productName: '',
        supplierId: '',
        supplierProductCode: '',
        categoryId: '',
        quantity: 1,
        unitCost: 0,
        productId: null,
        internalSku: '',
        isNewProduct: true,
        supplierName: '',
        dimensions: { height: 0, length: 0, width: 0, weight: 0 },
        description: '',
        price: 0.0,
        images: [],
        isActive: true,
        displaySettings: { onHomepage: false, onSeparamos: false },
      },
    ]);
  };

  const handleSavePurchase = async () => {
    // Validação da NF
    if (!invoiceNumber.trim()) {
      openModal(
        <p className="text-red-500">Por favor, preencha o Número da Nota Fiscal.</p>,
        'Campo Obrigatório'
      );
      return;
    }

    // Validação dos itens para fluxo COMPRA_BRUTO:
    // Fornecedor, código fornecedor, NOME, CATEGORIA, CUSTO BRUTO > 0 e PESO > 0.
    if (
      items.some(
        (item) =>
          !item.supplierId ||
          !item.supplierProductCode ||
          item.dimensions.weight <= 0 ||
          (item.isNewProduct &&
            (!item.productName || !item.categoryId || item.unitCost <= 0))
      )
    ) {
      openModal(
        <p className="text-red-500">
          Por favor, preencha todos os campos obrigatórios dos itens. Para produtos novos,
          Nome, Categoria, Custo Bruto &gt; 0 e Peso (g) são essenciais.
        </p>,
        'Campos Obrigatórios'
      );
      return;
    }

    setIsSaving(true);

    try {
      const processedItems = await Promise.all(
        items.map(async (item) => {
          let productDocId = item.productId;
          let finalInternalSku = item.internalSku;

          // Objeto base do produto mestre
          let productDataToSync = {
            name: item.productName,
            cost: Number(item.unitCost), // neste fluxo, temporariamente igual ao custo bruto
            supplierItemCode: item.supplierProductCode,
            stock: Number(item.quantity),
            description: item.description,
            price: Number(item.price),
            isActive: item.isActive,
            displaySettings: item.displaySettings,
            dimensions: item.dimensions,
            images: item.images,
            categoryId: item.categoryId,
            sku: item.internalSku,
          };

          // NOVO: para produtos NOVOS criados via COMPRA_BRUTO,
          // o modo de produção é BRUTO_BANHO e o custo bruto é explicitamente registrado.
          if (item.isNewProduct || !productDocId) {
            productDataToSync = {
              ...productDataToSync,
              productionMode: 'BRUTO_BANHO',
              rawCost: Number(item.unitCost),
              // cost permanece igual ao rawCost neste momento (antes do banho)
              cost: Number(item.unitCost),
              supplierRawId: item.supplierId,
              // Campos de banho e rastreabilidade serão preenchidos no fluxo BANHO / rastreabilidade
              // platingPurchaseId, platingCost, goldGramPrice, supplierPlatingId, rawPurchaseId
            };

            console.log(
              `[NewPurchasePage/COMPRA_BRUTO] Criando novo produto BRUTO_BANHO para ${item.supplierProductCode}`
            );
            const newProductRef = await productService.addProduct(productDataToSync);
            productDocId = newProductRef.id;
            finalInternalSku = productDataToSync.sku;
          } else {
            // Produto existente, atualizar (sem alterar estrutura de produtos antigos)
            console.log(
              `[NewPurchasePage/COMPRA_BRUTO] Atualizando produto mestre ${productDocId} para ${item.supplierProductCode}`
            );
            const existingProductData = await productService.getProduct(productDocId);
            if (existingProductData) {
              productDataToSync.stock =
                (existingProductData.stock || 0) + Number(item.quantity);
              productDataToSync.name =
                existingProductData.name || productDataToSync.name;
              productDataToSync.description =
                existingProductData.description || productDataToSync.description;
              productDataToSync.price =
                existingProductData.price || productDataToSync.price;
              productDataToSync.isActive =
                existingProductData.isActive ?? productDataToSync.isActive;
              productDataToSync.displaySettings =
                existingProductData.displaySettings || productDataToSync.displaySettings;
              productDataToSync.images =
                existingProductData.images || productDataToSync.images;
              // Dimensões são atualizadas a partir do item
              productDataToSync.dimensions =
                item.dimensions || existingProductData.dimensions;
              // O SKU permanece o do produto existente
              productDataToSync.sku = existingProductData.sku;
              finalInternalSku = existingProductData.sku;

              // Não forçamos productionMode/rawCost aqui para não alterar produtos antigos.
            } else {
              console.warn(
                `[NewPurchasePage/COMPRA_BRUTO] Produto com ID ${productDocId} não encontrado. Recriando como novo BRUTO_BANHO.`
              );
              productDataToSync = {
                ...productDataToSync,
                productionMode: 'BRUTO_BANHO',
                rawCost: Number(item.unitCost),
                cost: Number(item.unitCost),
                supplierRawId: item.supplierId,
              };
              const newProductRef = await productService.addProduct(productDataToSync);
              productDocId = newProductRef.id;
              finalInternalSku = productDataToSync.sku;
            }

            await productService.updateProduct(productDocId, productDataToSync);
          }

          return {
            ...item,
            productId: productDocId,
            internalSku: finalInternalSku,
          };
        })
      );

      // Seleciona o friendlyCode do primeiro item, assumindo 1 fornecedor por compra
      const mainSupplier = suppliers.find((s) => s.id === items[0].supplierId);
      const supplierFriendlyCode = mainSupplier ? mainSupplier.friendlyCode : '';

      const purchaseData = {
        purchaseDate: Timestamp.fromDate(new Date(purchaseDate)),
        invoiceNumber: invoiceNumber,
        supplierId: items[0].supplierId,
        supplierName: mainSupplier ? mainSupplier.name : '',
        supplierFriendlyCode: supplierFriendlyCode,
        // NOVO: tipo da compra para diferenciar fluxos
        purchaseType: 'COMPRA_BRUTO',
        items: processedItems.map((item) => ({
          productName: item.productName,
          supplierId: item.supplierId,
          supplierProductCode: item.supplierProductCode,
          categoryId: item.categoryId,
          quantity: Number(item.quantity),
          unitCost: Number(item.unitCost), // aqui representa o CUSTO BRUTO
          // NOVO: Registro explícito do peso em gramas e custo bruto no item da compra
          weight: Number(item.dimensions?.weight || 0),
          rawCost: Number(item.unitCost),
          productId: item.productId,
          internalSku: item.internalSku,
          isNewProduct: item.isNewProduct,
        })),
        totalPurchaseValue: totalPurchase,
        status: 'Concluída',
        createdAt: Timestamp.now(),
      };

      await addPurchase(purchaseData);
      openModal(
        <p className="text-green-500">
          A compra BRUTO foi registrada com sucesso e os produtos foram sincronizados como
          BRUTO_BANHO!
        </p>,
        'Compra Registrada!'
      );
      resetForm();
    } catch (error) {
      console.error(
        'Erro detalhado ao salvar a compra COMPRA_BRUTO e sincronizar produtos: ',
        error
      );
      openModal(
        <p className="text-red-500">
          Ocorreu um erro ao salvar a compra. Verifique o console (F12) para mais detalhes
          técnicos e, se necessário, contate o suporte.
        </p>,
        'Erro ao Salvar'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-8 bg-brand-black text-white font-sans">
      <h1 className="text-3xl font-bold font-serif mb-6">
        Registrar Nova Compra de Peça Bruta (COMPRA_BRUTO)
      </h1>

      <div className="bg-brand-dark p-6 rounded-lg mb-6 border border-gray-700">
        <h2 className="text-xl font-semibold mb-4 text-gray-200">Detalhes da Compra</h2>
        <label
          htmlFor="purchaseDate"
          className="block text-sm font-medium text-gray-400"
        >
          Data da Compra
        </label>
        <input
          type="date"
          id="purchaseDate"
          value={purchaseDate}
          onChange={(e) => setPurchaseDate(e.target.value)}
          className="mt-1 block w-full md:w-1/3 bg-gray-800 border-gray-600 rounded-md shadow-sm p-2 text-white focus:ring-brand-gold focus:border-brand-gold"
        />

        <label
          htmlFor="invoiceNumber"
          className="block text-sm font-medium text-gray-400 mt-4"
        >
          Número da Nota Fiscal
        </label>
        <input
          type="text"
          id="invoiceNumber"
          value={invoiceNumber}
          onChange={(e) => setInvoiceNumber(e.target.value)}
          className="mt-1 block w-full md:w-1/3 bg-gray-800 border-gray-600 rounded-md shadow-sm p-2 text-white focus:ring-brand-gold focus:border-brand-gold"
          placeholder="Ex: 123456789 ou ABC-XYZ"
        />
      </div>

      <div className="bg-brand-dark p-6 rounded-lg mb-6 border border-gray-700">
        <h2 className="text-xl font-semibold mb-4 text-gray-200">Itens da Compra</h2>
        {items.map((item, index) => (
          <div
            key={index}
            className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4 p-4 border border-gray-700 rounded-lg relative"
          >
            {/* Botão de Remover Item */}
            {items.length > 1 && (
              <button
                onClick={() => handleRemoveItem(index)}
                className="absolute top-2 right-2 text-red-500 hover:text-red-400 transition-colors"
                title="Remover Item"
              >
                <X size={18} />
              </button>
            )}

            {/* Fornecedor */}
            <div className="col-span-12 md:col-span-2">
              <label className="text-sm font-medium text-gray-400">Fornecedor*</label>
              <div className="flex items-center space-x-2">
                <select
                  name="supplierId"
                  value={item.supplierId}
                  onChange={(e) => handleItemChange(index, e)}
                  className="form-select w-full mt-1 bg-gray-800 p-2 rounded-md border-gray-600 text-white focus:ring-brand-gold focus:border-brand-gold"
                >
                  <option value="">Selecione...</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleAddNewSupplier}
                  className="mt-1 p-2 bg-brand-gold text-brand-black rounded-md hover:bg-yellow-500"
                  title="Adicionar Novo Fornecedor"
                >
                  <Plus size={20} />
                </button>
              </div>
            </div>

            {/* Cód. Prod. Fornecedor */}
            <div className="col-span-12 md:col-span-2">
              <label className="text-sm font-medium text-gray-400">
                Cód. Prod. Fornecedor*
              </label>
              <input
                name="supplierProductCode"
                value={item.supplierProductCode}
                onChange={(e) => handleItemChange(index, e)}
                className="w-full mt-1 bg-gray-800 p-2 rounded-md border-gray-600 text-white"
                placeholder="Código na nota"
                disabled={!item.supplierId}
              />
              {item.internalSku && (
                <p className="text-xs text-gray-500 mt-1">SKU GS: {item.internalSku}</p>
              )}
              {item.productId && (
                <p className="text-xs text-gray-500 mt-1">
                  ID Prod: {item.productId.substring(0, 6)}...
                </p>
              )}
              {item.isNewProduct &&
                item.supplierProductCode &&
                item.supplierId &&
                item.categoryId && (
                  <p className="text-xs text-yellow-500 mt-1">
                    PRODUTO NOVO (preencha abaixo)
                  </p>
                )}
            </div>

            {/* Nome do Produto */}
            <div className="col-span-12 md:col-span-3">
              <label className="text-sm font-medium text-gray-400">
                Nome da Peça Bruta*
              </label>
              <input
                name="productName"
                value={item.productName}
                onChange={(e) => handleItemChange(index, e)}
                className="w-full mt-1 bg-gray-800 p-2 rounded-md border-gray-600 text-white"
                placeholder="Ex: Anel Vazado (bruto)"
                disabled={item.productId !== null && !item.isNewProduct}
              />
            </div>

            {/* Categoria */}
            <div className="col-span-12 md:col-span-2">
              <label className="text-sm font-medium text-gray-400">Categoria*</label>
              <select
                name="categoryId"
                value={item.categoryId}
                onChange={(e) => handleItemChange(index, e)}
                className="form-select w-full mt-1 bg-gray-800 p-2 rounded-md border-gray-600 focus:ring-brand-gold focus:border-brand-gold text-white"
                disabled={item.productId !== null && !item.isNewProduct}
              >
                <option value="">Selecione...</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Quantidade */}
            <div className="col-span-6 md:col-span-1">
              <label className="text-sm font-medium text-gray-400">Qtde.</label>
              <input
                type="number"
                name="quantity"
                value={item.quantity}
                onChange={(e) => handleItemChange(index, e)}
                className="w-full mt-1 bg-gray-800 p-2 rounded-md border-gray-600 text-white"
                min="1"
              />
            </div>

            {/* Custo Bruto (R$) – agora com MoneyInput */}
            <div className="col-span-6 md:col-span-2">
              <label className="text-sm font-medium text-gray-400">
                Custo Bruto da Peça (R$)
              </label>
              <div className="mt-1">
                <MoneyInput
                  name={`unitCost-${index}`}
                  valueInCents={Math.round((Number(item.unitCost) || 0) * 100)}
                  onValueChange={(cents) => handleItemMoneyChange(index, 'unitCost', cents)}
                />
              </div>
            </div>

            {/* Dimensões do Produto */}
            <h4 className="col-span-full text-sm font-semibold text-gray-500 mt-2">
              Dimensões da Peça Bruta:
            </h4>
            <div className="col-span-3">
              <label className="text-sm font-medium text-gray-400">Altura (cm)</label>
              <input
                type="number"
                step="0.01"
                value={item.dimensions.height}
                onChange={(e) =>
                  handleItemDimensionChange(index, 'height', e.target.value)
                }
                className="w-full mt-1 bg-gray-800 p-2 rounded-md border-gray-600 text-white"
                placeholder="0"
              />
            </div>
            <div className="col-span-3">
              <label className="text-sm font-medium text-gray-400">
                Comprimento (cm)
              </label>
              <input
                type="number"
                step="0.01"
                value={item.dimensions.length}
                onChange={(e) =>
                  handleItemDimensionChange(index, 'length', e.target.value)
                }
                className="w-full mt-1 bg-gray-800 p-2 rounded-md border-gray-600 text-white"
                placeholder="0"
              />
            </div>
            <div className="col-span-3">
              <label className="text-sm font-medium text-gray-400">Largura (cm)</label>
              <input
                type="number"
                step="0.01"
                value={item.dimensions.width}
                onChange={(e) =>
                  handleItemDimensionChange(index, 'width', e.target.value)
                }
                className="w-full mt-1 bg-gray-800 p-2 rounded-md border-gray-600 text-white"
                placeholder="0"
              />
            </div>
            <div className="col-span-3">
              <label className="text-sm font-medium text-gray-400">Peso (g)*</label>
              <input
                type="number"
                step="0.01"
                value={item.dimensions.weight}
                onChange={(e) =>
                  handleItemDimensionChange(index, 'weight', e.target.value)
                }
                className="w-full mt-1 bg-gray-800 p-2 rounded-md border-gray-600 text-white"
                placeholder="0"
              />
            </div>
          </div>
        ))}
        <button
          onClick={handleAddItem}
          className="flex items-center text-brand-gold hover:text-yellow-500 font-semibold mt-4"
        >
          <Plus size={18} className="mr-2" /> Adicionar Item
        </button>
      </div>

      <div className="bg-gray-800 p-4 rounded-lg flex justify-end items-center space-x-6">
        <div className="text-right">
          <p className="text-gray-400">
            Subtotal dos Itens:{' '}
            <span className="font-semibold text-white">
              R$ {subtotalItems.toFixed(2).replace('.', ',')}
            </span>
          </p>
          <p className="text-xl font-bold text-brand-gold">
            Total da Compra: R$ {totalPurchase.toFixed(2).replace('.', ',')}
          </p>
        </div>
        <button
          onClick={handleSavePurchase}
          disabled={isSaving}
          className="bg-brand-gold text-brand-black font-bold py-3 px-6 rounded-lg hover:bg-yellow-500 disabled:bg-gray-500 disabled:cursor-not-allowed transition-all duração-300 flex items-center justify-center"
          style={{ minWidth: '150px' }}
        >
          {isSaving ? <Loader2 className="animate-spin" /> : 'Salvar Compra BRUTO'}
        </button>
      </div>
    </div>
  );
};

export default NewPurchasePage;
